import React, {useEffect, useState, useCallback} from 'react';
import {View, FlatList, StyleSheet, RefreshControl, Alert} from 'react-native';
import {Text, IconButton, Snackbar, Button, Chip} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import EditPurchaseModal from './EditPurchaseModal';
import DateTimePicker from '@react-native-community/datetimepicker';
import {colors, spacing} from '../../theme';

const STATUS_COLORS = {
  ORDERED: '#F57C00',
  RECEIVED: '#388E3C',
  CANCELLED: '#D32F2F',
};

const PurchasesTab = ({selectedOrgUnitId}) => {
  const {t} = useTranslation();
  const {
    purchaseOrders, isLoading, fetchPurchaseOrders,
    deletePurchaseOrder, updatePurchaseOrderStatus,
  } = useInventoryCatalogStore();
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});
  const [editModal, setEditModal] = useState({visible: false, item: null});

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);

  const formatDate = (date) => date.toISOString().split('T')[0];
  const formatDisplay = (date) => date.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'});

  useEffect(() => {
    fetchPurchaseOrders(formatDate(fromDate), formatDate(toDate), undefined, selectedOrgUnitId);
  }, [fetchPurchaseOrders, fromDate, toDate, selectedOrgUnitId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchPurchaseOrders(formatDate(fromDate), formatDate(toDate), undefined, selectedOrgUnitId);
    } finally {
      setRefreshing(false);
    }
  }, [fetchPurchaseOrders, fromDate, toDate, selectedOrgUnitId]);

  const handleDelete = useCallback(async (item) => {
    try {
      await deletePurchaseOrder(item.id);
      setSnackbar({visible: true, message: 'Purchase deleted'});
    } catch (e) {
      setSnackbar({visible: true, message: e.response?.data?.message || t('common.error')});
    }
  }, [deletePurchaseOrder, t]);

  const handleReceive = useCallback(async (item) => {
    Alert.alert(
      t('inventory.receiveStock'),
      t('inventory.receiveConfirm'),
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.confirm'),
          onPress: async () => {
            try {
              await updatePurchaseOrderStatus(item.id, 'RECEIVED');
              setSnackbar({visible: true, message: t('inventory.statusReceived')});
            } catch (e) {
              setSnackbar({visible: true, message: e.response?.data?.message || t('common.error')});
            }
          },
        },
      ],
    );
  }, [updatePurchaseOrderStatus, t]);

  const handleCancel = useCallback(async (item) => {
    Alert.alert(
      t('inventory.cancelOrder'),
      t('inventory.cancelConfirm'),
      [
        {text: t('common.no'), style: 'cancel'},
        {
          text: t('common.yes'),
          style: 'destructive',
          onPress: async () => {
            try {
              await updatePurchaseOrderStatus(item.id, 'CANCELLED');
              setSnackbar({visible: true, message: t('inventory.statusCancelled')});
            } catch (e) {
              setSnackbar({visible: true, message: e.response?.data?.message || t('common.error')});
            }
          },
        },
      ],
    );
  }, [updatePurchaseOrderStatus, t]);

  const renderItem = useCallback(({item: po}) => {
    const isOrdered = po.purchaseStatus === 'ORDERED';
    const statusColor = STATUS_COLORS[po.purchaseStatus] || colors.textSecondary;
    const statusLabel = t(`inventory.status${po.purchaseStatus.charAt(0) + po.purchaseStatus.slice(1).toLowerCase()}`);

    return (
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderLeft}>
            <Text style={styles.orderNumber}>{po.orderNumber}</Text>
            <Text style={styles.orderDate}>{new Date(po.orderDate).toLocaleDateString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}</Text>
          </View>
          <Chip
            compact
            style={[styles.statusChip, {backgroundColor: statusColor + '20'}]}>
            <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{statusLabel}</Text>
          </Chip>
        </View>

        {po.lines && po.lines.map((line, idx) => {
          const badge = line.catalogItemType === 'MATERIAL' ? 'M' : 'R';
          const badgeColor = line.catalogItemType === 'MATERIAL' ? colors.primary : colors.secondary;

          return (
            <View key={line.id || idx} style={styles.lineRow}>
              <View style={[styles.badge, {backgroundColor: badgeColor}]}>
                <Text style={styles.badgeText}>{badge}</Text>
              </View>
              <Text style={styles.lineName} numberOfLines={1}>{line.catalogItemName}</Text>
              <Text style={styles.lineDetail}>
                {line.quantity} x {'\u20B9'}{line.unitPrice} = {'\u20B9'}{line.totalPrice}
              </Text>
            </View>
          );
        })}

        <View style={styles.cardFooter}>
          <Text style={styles.totalLabel}>
            {t('order.totalAmount')}: {'\u20B9'}{po.totalAmount}
          </Text>
          <View style={styles.cardActions}>
            {isOrdered && (
              <>
                <IconButton
                  icon="pencil-outline"
                  size={20}
                  onPress={() => setEditModal({visible: true, item: po})}
                />
                <IconButton
                  icon="delete-outline"
                  size={20}
                  iconColor={colors.error}
                  onPress={() => handleDelete(po)}
                />
                <Button
                  mode="contained"
                  compact
                  onPress={() => handleReceive(po)}
                  style={styles.receiveBtn}
                  labelStyle={styles.receiveBtnLabel}>
                  {t('inventory.receiveStock')}
                </Button>
                <IconButton
                  icon="close-circle-outline"
                  size={20}
                  iconColor={colors.error}
                  onPress={() => handleCancel(po)}
                />
              </>
            )}
          </View>
        </View>
      </View>
    );
  }, [handleDelete, handleReceive, handleCancel, t]);

  return (
    <View style={styles.container}>
      <View style={styles.dateRow}>
        <Button
          mode="outlined"
          compact
          onPress={() => setShowFromPicker(true)}
          style={styles.dateButton}>
          {t('report.dateFrom')}: {formatDisplay(fromDate)}
        </Button>
        <Button
          mode="outlined"
          compact
          onPress={() => setShowToPicker(true)}
          style={styles.dateButton}>
          {t('report.dateTo')}: {formatDisplay(toDate)}
        </Button>
      </View>

      {showFromPicker && (
        <DateTimePicker
          value={fromDate}
          mode="date"
          onChange={(e, date) => {
            setShowFromPicker(false);
            if (date) setFromDate(date);
          }}
        />
      )}
      {showToPicker && (
        <DateTimePicker
          value={toDate}
          mode="date"
          onChange={(e, date) => {
            setShowToPicker(false);
            if (date) setToDate(date);
          }}
        />
      )}

      <FlatList
        data={purchaseOrders}
        renderItem={renderItem}
        keyExtractor={item => String(item.id)}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          !isLoading && <Text style={styles.emptyText}>{t('inventory.noPurchases')}</Text>
        }
      />

      <EditPurchaseModal
        visible={editModal.visible}
        item={editModal.item}
        onDismiss={() => setEditModal({visible: false, item: null})}
        onSuccess={() => {
          setEditModal({visible: false, item: null});
          fetchPurchaseOrders(formatDate(fromDate), formatDate(toDate), undefined, selectedOrgUnitId);
        }}
      />

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1},
  dateRow: {
    flexDirection: 'row',
    padding: spacing.sm,
    gap: spacing.sm,
  },
  dateButton: {flex: 1},
  list: {paddingBottom: spacing.xl, paddingHorizontal: spacing.sm},
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    marginBottom: spacing.sm,
    padding: spacing.md,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  cardHeaderLeft: {flex: 1},
  orderNumber: {fontSize: 14, fontWeight: '600', color: colors.text},
  orderDate: {fontSize: 12, color: colors.textSecondary, marginTop: 2},
  statusChip: {height: 28},
  statusText: {fontSize: 11, fontWeight: '600'},
  lineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  badgeText: {color: '#fff', fontSize: 10, fontWeight: '700'},
  lineName: {flex: 1, fontSize: 13, color: colors.text},
  lineDetail: {fontSize: 13, color: colors.textSecondary},
  cardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.sm,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
    paddingTop: spacing.sm,
  },
  totalLabel: {fontSize: 14, fontWeight: '600', color: colors.text},
  cardActions: {flexDirection: 'row', alignItems: 'center'},
  receiveBtn: {
    borderRadius: 8,
    paddingHorizontal: 4,
  },
  receiveBtnLabel: {fontSize: 11},
  emptyText: {textAlign: 'center', marginTop: spacing.xl, color: colors.textSecondary},
});

export default PurchasesTab;
